import React from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  const updatedOn = "January 2026";

  return (
    <div className="bg-[#f3f0e4] text-[#105132]">
      {/* Hero */}
      <div className="border-b border-[#105132]/10 bg-white/60 backdrop-blur">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#105132]/15 bg-white px-4 py-2 text-xs font-extrabold tracking-wide">
            <span className="h-2 w-2 rounded-full bg-[#c0a230]" />
            Legal
          </div>

          <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight">
            Privacy Policy
          </h1>

          <p className="mt-3 text-[#105132]/75 leading-relaxed">
            This Privacy Policy explains how <span className="font-bold">The NutraFox</span> collects,
            uses, shares, and protects your information when you use our website and services.
          </p>

          <div className="mt-4 flex flex-wrap gap-3 text-sm text-[#105132]/70">
            <span className="rounded-full bg-[#105132]/5 px-4 py-2 border border-[#105132]/10">
              Effective: <span className="font-bold">{updatedOn}</span>
            </span>
            <span className="rounded-full bg-[#105132]/5 px-4 py-2 border border-[#105132]/10">
              Website: <span className="font-bold">thenutrafox.com</span>
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
        <div className="rounded-3xl border border-[#105132]/10 bg-white shadow-sm overflow-hidden">
          <div className="p-6 sm:p-8">
            <Section title="1. Who We Are">
              <p>
                The NutraFox (“we”, “our”, “us”) operates an e-commerce and export enquiry platform for
                makhana (fox nuts) and related products. This policy applies to information collected
                via our website and forms.
              </p>
            </Section>

            <Section title="2. Information We Collect">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <b>Personal details:</b> name, email, phone number.
                </li>
                <li>
                  <b>Order & shipping details:</b> address, city/state, pincode, products purchased,
                  order notes.
                </li>
                <li>
                  <b>Export enquiry details:</b> country, product interest, quantity, message and
                  business details you share.
                </li>
                <li>
                  <b>Technical & usage data:</b> IP address, device/browser info, pages visited,
                  interactions and basic analytics.
                </li>
              </ul>
            </Section>

            <Section title="3. How We Use Your Information">
              <ul className="list-disc pl-5 space-y-2">
                <li>To process orders and deliver products.</li>
                <li>To respond to customer support and export enquiries.</li>
                <li>To send order updates, confirmations and important service messages.</li>
                <li>To improve website performance, user experience, and product offerings.</li>
                <li>
                  To measure marketing and website performance using analytics tools (only in
                  aggregated form wherever possible).
                </li>
              </ul>
            </Section>

            <Section title="4. Payments">
              <p>
                We do <b>not</b> store your card number, CVV, or net-banking credentials. Payments
                are processed securely by trusted third-party payment gateways, and we only receive
                payment status/confirmation details.
              </p>
            </Section>

            <Section title="5. Cookies & Tracking">
              <p>
                We may use cookies and similar technologies to keep the site functional and to
                understand how visitors use the website (for example, through analytics).
              </p>
              <p className="mt-3">
                You can control cookies through your browser settings. Disabling cookies may affect
                certain features.
              </p>
            </Section>

            <Section title="6. Sharing of Information">
              <p>We may share your information with:</p>
              <ul className="list-disc pl-5 space-y-2 mt-2">
                <li><b>Delivery/logistics partners</b> to fulfill shipments.</li>
                <li><b>Payment gateways</b> to process payments securely.</li>
                <li><b>Analytics & marketing tools</b> to understand site performance.</li>
                <li><b>Legal/regulatory authorities</b> when required by applicable law.</li>
              </ul>
              <p className="mt-3">
                We do <b>not</b> sell or rent your personal information to third parties.
              </p>
            </Section>

            <Section title="7. Data Security">
              <p>
                We use reasonable administrative and technical measures to protect your information.
                However, no online platform can guarantee absolute security.
              </p>
            </Section>

            <Section title="8. Data Retention">
              <p>
                We retain personal information only as long as necessary for order fulfillment,
                customer support, legal compliance, and legitimate business purposes.
              </p>
            </Section>

            <Section title="9. Your Rights">
              <ul className="list-disc pl-5 space-y-2">
                <li>Request access to your personal data.</li>
                <li>Request correction of inaccurate information.</li>
                <li>Request deletion (subject to legal/operational requirements).</li>
                <li>Opt-out of marketing communications anytime.</li>
              </ul>
            </Section>

            <Section title="10. International Users">
              <p>
                If you access our website from outside India, your information may be processed in
                India. We follow privacy principles aligned with applicable data protection norms
                wherever relevant.
              </p>
            </Section>

            <Section title="11. Changes to This Policy">
              <p>
                We may update this policy from time to time. Any updates will be posted on this page
                with a revised effective date.
              </p>
            </Section>

            <Section title="12. Contact Us">
              <p>
                If you have questions about this Privacy Policy or how we handle your data, contact
                us:
              </p>

              <div className="mt-4 rounded-2xl border border-[#105132]/10 bg-[#f3f0e4] p-4">
                <div className="text-sm font-extrabold">The NutraFox</div>
                <div className="text-sm text-[#105132]/75 mt-1">
                  Email:{" "}
                  <a
                    className="font-bold underline hover:text-[#c0a230]"
                    href="mailto:thenutrafox@gmail.com"
                  >
                    thenutrafox@gmail.com
                  </a>
                </div>
              </div>

              <div className="mt-6 text-sm text-[#105132]/70">
                <Link to="/" className="font-bold underline hover:text-[#c0a230]">
                  ← Back to Home
                </Link>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="py-6 border-b border-[#105132]/10 last:border-b-0">
      <h2 className="text-lg sm:text-xl font-extrabold">{title}</h2>
      <div className="mt-3 text-[#105132]/80 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}
